import { motion } from "motion/react";
import { useRef, useState, type Dispatch, type PointerEvent } from "react";
import type { TodoType, Action } from "@/types/types";

export default function Todo({
  todo,
  dispatch,
}: {
  todo: TodoType;
  dispatch: Dispatch<Action>;
}) {
  const isDragging = useRef(false);
  const movementY = useRef(0);
  const activeDropZone = useRef<Element | null>(null);
  const elementRef = useRef<HTMLLIElement>(null);
  function startDrag(e: PointerEvent) {
    if (elementRef.current) {
      movementY.current = 0;
      isDragging.current = true;
      e.currentTarget.setPointerCapture(e.pointerId);
      elementRef.current.classList.add("dragging");
      elementRef.current.style.transform = `translateY(${e.movementY}px)`;
    }
  }

  function endDrag(e: PointerEvent) {
    if (elementRef.current) {
      isDragging.current = false;
      e.currentTarget.releasePointerCapture(e.pointerId);

      if (activeDropZone.current) {
        activeDropZone.current.classList.remove("drop-target");
        activeDropZone.current = null;
      }

      elementRef.current.style.visibility = "hidden";
      const dropTarget = document.elementFromPoint(e.clientX, e.clientY);
      elementRef.current.style.visibility = "visible";

      const dropZone = dropTarget?.closest("[data-date]");
      if (dropZone) {
        const targetDate = dropZone.getAttribute("data-date");
        if (targetDate)
          dispatch({
            type: "update",
            payload: { ...todo, doneBy: new Date(targetDate) },
          });
      }
      elementRef.current.classList.remove("dragging");
      elementRef.current.style.translate = "unset";
    }
  }

  function drag(e: PointerEvent) {
    if (elementRef.current && isDragging.current) {
      movementY.current += e.movementY;
      elementRef.current.style.translate = `0 ${movementY.current}px`;

      elementRef.current.style.visibility = "hidden";
      const dropTarget = document.elementFromPoint(e.clientX, e.clientY);
      elementRef.current.style.visibility = "visible";

      const dropZone = dropTarget?.closest("[data-date]");

      if (dropZone !== activeDropZone.current) {
        if (activeDropZone.current) {
          activeDropZone.current.classList.remove("drop-target");
        }
        if (dropZone) {
          dropZone.classList.add("drop-target");
        }
        activeDropZone.current = dropZone || null;
      }
    }
  }

  return (
    <motion.li
      exit={{ opacity: 0, translateX: 50, height: 0 }}
      ref={elementRef}
      // onPointerDown={startDrag}
      // onPointerUp={endDrag}
      // onPointerMove={drag}
      className={`todo ${todo.done ? "done" : ""}`}
    >
      <input
        type="checkbox"
        checked={todo.done}
        onChange={() =>
          dispatch({ type: "update", payload: { ...todo, done: !todo.done } })
        }
      />

      <input
        className="title"
        defaultValue={todo.title}
        onInput={(e) =>
          dispatch({
            type: "update",
            payload: { ...todo, title: e.currentTarget.value.trim() },
          })
        }
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
        }}
      />
      <button
        className="delete"
        onClick={() => dispatch({ type: "delete", payload: todo })}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
          />
        </svg>
      </button>
    </motion.li>
  );
}
